import { CookieCategories } from "./types";

/**
 * Checks if the user has consented to a specific cookie category.
 *
 * @param cookieType - The type of cookie category to check, represented as a key of the CookieCategories interface.
 *         If null, it will check if CookieYes is initialized and the user has made their selection (no cookie check).
 * @param onlyGdpr - A boolean indicating whether to only enforce this for GDPR regions. This is because in non-GDPR
 *                   regions, we only need consent to store cookies that have PII.
 * @returns A boolean indicating whether the user has consented to the specified cookie category.
 */
export const isCookieConsented = (cookieType: keyof CookieCategories | null, onlyGdpr: boolean = true): boolean => {
  if (process.env.NODE_ENV === "development") {
    return true;
  }

  if (window.getCkyConsent) {
    const cookieConsent = window.getCkyConsent();
    if ((onlyGdpr && cookieConsent.activeLaw !== 'gdpr') 
      || (cookieConsent.isUserActionCompleted && (!cookieType || cookieConsent.categories[cookieType]))) {
      return true;
    } else {
      return false;
    }
  } else {
    console.log("CookieYes not initialized");
    return false;
  }
};

export const runWhenCookieConsented = (
  cookieType: keyof CookieCategories | null, onlyGdpr: boolean, 
  consentedCallback: () => void, notConsentedCallback?: () => void
): void => {
  const runIfConsented = () => {
    if (isCookieConsented(cookieType, onlyGdpr)) {
      consentedCallback();
    } else if (notConsentedCallback) {
      notConsentedCallback();
    }
  };

  if (window.getCkyConsent) {
    runIfConsented();
  } else {
    // This is a workaround for CookieYes not being initialized yet.
    const interval = setInterval(() => {
      if (window.getCkyConsent) {
        runIfConsented();
        clearInterval(interval);
      }
    }, 1000);
    // The listener doesn't fire when CookieYes is initialized, only when choices are updated.
    //document.addEventListener('cookieyes_consent_update', runIfConsented);
  }
};